import React, { useState, useEffect, useRef } from 'react';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { DeviceEUIProps, eSensorChartAtom, EsensorChart, ParsedSensorInfo, sensorInfoAtom } from '../atoms-types/eSensors';
import { isSensorOffline } from '../atoms-types/sensorFreshness';
import { Card, CardContent, Typography, Box } from '@mui/material';
import moment, { Moment } from 'moment-timezone';
import DownloadButton from '../buttons/downloadButton';
import SensorGraph from '../charts-graphs/sensorGraph';
import GraphTimePicker from '../buttons/graphTimePicker';
import { endpointLink } from '../../index';

interface ExtendedDeviceEUIProps extends DeviceEUIProps {
  subClass?: 'temp' | 'wind' | null;
  sensorColor?: string;
  sensorInfo?: ParsedSensorInfo;
  onRangeChange?: (range: string) => void;
  startDateTime?: Moment;
  endDateTime?: Moment;
  setStartDateTime?: (date: Moment) => void;
  setEndDateTime?: (date: Moment) => void;
  /** When true, omit the built-in time-range picker (parent owns it). */
  hideTimePicker?: boolean;
  chartTitle?: string;
  /** Tighter paddings for embedded HVAC layout. */
  compact?: boolean;
  selectedRange?: string;
  setSelectedRange?: (range: string) => void;
}

const EsensorChartHandler: React.FC<ExtendedDeviceEUIProps> = ({
  deviceEUI,
  Display,
  subClass,
  sensorColor,
  sensorInfo,
  onRangeChange,
  startDateTime: externalStart,
  endDateTime: externalEnd,
  setStartDateTime: externalSetStart,
  setEndDateTime: externalSetEnd,
  hideTimePicker = false,
  chartTitle,
  compact = false,
  selectedRange: externalRange,
  setSelectedRange: externalSetRange,
}) => {
  const setEsensorChart = useSetAtom(eSensorChartAtom);
  const esensorChartData = useAtomValue(eSensorChartAtom);
  const [sensorInfoAtomValue] = useAtom(sensorInfoAtom);
  const [internalStart, setInternalStart] = useState<Moment>(moment().subtract(24, 'hour').tz('America/New_York'));
  const [internalEnd, setInternalEnd] = useState<Moment>(moment().tz('America/New_York'));
  const startDateTime = externalStart ?? internalStart;
  const endDateTime = externalEnd ?? internalEnd;
  const setStartDateTime = externalSetStart ?? setInternalStart;
  const setEndDateTime = externalSetEnd ?? setInternalEnd;
  const [internalRange, setInternalRange] = useState<string>("Last 24 hours");
  const selectedRange = externalRange ?? internalRange;
  const setSelectedRange = externalSetRange ?? setInternalRange;

  // Notify parent when range changes (only when parent isn't already controlling it)
  useEffect(() => {
    if (!externalSetRange) {
      onRangeChange?.(selectedRange);
    }
  }, [selectedRange, onRangeChange, externalSetRange]);

  // Track the last live reading we appended so unrelated WS updates (other
  // devices) — or identical re-delivers — don't keep growing the chart.
  // FridgeTemp payloads often omit Timestamp; falling back to Date.now() on
  // every atom change was appending a new point per WS tick for any device.
  // The ref is also cleared whenever the history is (re)loaded (see
  // UpdateSensorGraph) so the current live reading gets re-appended onto the
  // fresh dataset instead of being swallowed by the dedupe guard.
  const lastLiveReadingRef = useRef<string>('');
  useEffect(() => {
    lastLiveReadingRef.current = '';
  }, [deviceEUI]);

  // Update chart data when the *selected* sensor's reading changes
  useEffect(() => {
    if (!deviceEUI || selectedRange === "Custom") return;
    
    const currentSensorInfo = sensorInfoAtomValue[deviceEUI];
    if (!currentSensorInfo || currentSensorInfo.length === 0) return;
    
    const sensor = currentSensorInfo[0];
    if (!sensor.jInfo) return;

    // Don't synthesize a "now" data point for a sensor that hasn't reported
    // within the freshness window. These payloads often omit Timestamp, so the
    // fallback below would stamp the last-known (possibly months-old) reading at
    // the current time and make an offline sensor look like it has live data.
    if (isSensorOffline(sensor.LastRx)) return;

    const readingKey = JSON.stringify(sensor.jInfo);
    if (readingKey === lastLiveReadingRef.current) return;
    lastLiveReadingRef.current = readingKey;
    
    // Prefer payload timestamp; otherwise one stable "now" for this reading
    const timestamp = (sensor.jInfo as any).Timestamp || new Date().toISOString();
    
    // Filter displays based on subClass for weather sensors
    let filteredDisplays = Display;
    if (subClass) {
      if (subClass === 'temp') {
        filteredDisplays = Display.filter(d => ['TempF', 'Humidity'].includes(d));
      } else if (subClass === 'wind') {
        filteredDisplays = Display.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
      }
    }

    // Create an object with all matching display values
    const jInfoObj = sensor.jInfo as Record<string, any>;
    const displayValues = Array.isArray(filteredDisplays) 
      ? filteredDisplays.reduce((acc, key) => {
          // Use the same logic as the cards
          const value = getSensorValue(jInfoObj, sensor.Type, key);
          acc[key] = value;
          return acc;
        }, {} as Record<string, number>)
      : { [filteredDisplays]: getSensorValue(jInfoObj, sensor.Type, filteredDisplays) };

    setEsensorChart(prev => {
      // Check if data with this timestamp already exists
      const timestampExists = prev.some(data => data.timestamp === timestamp);
      if (timestampExists) {
        return prev; // Return previous state if timestamp exists
      }
      
      // Add new data if timestamp doesn't exist
      const chartData: EsensorChart = {
        timestamp,
        ...displayValues
      };
      return [...prev, chartData];
    });
    // esensorChartData is a trigger dep: after a history reload replaces the
    // atom (and resets the dedupe ref), this re-runs to re-append the live
    // reading. Once appended, the readingKey guard above short-circuits it.
  }, [sensorInfoAtomValue, deviceEUI, Display, subClass, selectedRange, setEsensorChart, esensorChartData]);

  // Create filtered display array for the chart
  const getFilteredDisplays = () => {
    if (subClass) {
      if (subClass === 'temp') {
        return Display.filter(d => ['TempF', 'Humidity'].includes(d));
      } else if (subClass === 'wind') {
        return Display.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
      }
    }
    return Display;
  };

  // Use the same logic as the cards to get sensor values
  const getSensorValue = (sensorData: any, sensorType: string, display: string): number => {
    // Type mapping for sensor types (same as in environmentalGrid)
    const sensorTypeMap: Record<string, any> = {
      "Weather": {},
      "RainGauge": {},
      "RiverHeight": {},
      "Tank": {},
      "Kiln": {},
      "SoilMt": {},
      "FridgeTemp": {}
    };
    
    const type = sensorTypeMap[sensorType];
    if (!type) return 0;
    
    const value = (sensorData as typeof type)[display as keyof typeof type];
    return typeof value === 'number' ? value : 0;
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ['EsensorChart', deviceEUI, startDateTime, endDateTime],
    // Only fetch history on load and on graph changes (device/date/subClass),
    // which are driven by the explicit refetch() effects below. New points
    // arrive live from the websocket, so never background/focus/reconnect
    // refetch — that just reloads the chart and fights the live updates.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    queryFn: async () => {
      if (!deviceEUI) return [];
      const response = await axios.get(
        `${endpointLink}esensorchart/${deviceEUI}`, {
          params: {
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString()
          }
        }
      );
      const data = response.data;
      console.log("EsensorChart data", data);
      UpdateSensorGraph(data);
      return data;
    },
    enabled: !!deviceEUI && !!Display,
  });
    
  useEffect(() => {
    refetch();
  }, [deviceEUI, startDateTime, endDateTime, refetch]);

  useEffect(() => {
    if (deviceEUI) {
      refetch();
    }
  }, [subClass, deviceEUI, refetch]);

  function UpdateSensorGraph(data: { Timestamp: string; jInfo: string }[]) {
    const updatedEsensorChart: EsensorChart[] = data?.map((entry) => {
      const parsedJInfo = JSON.parse(entry.jInfo || '{}');
      
      // Filter displays based on subClass for weather sensors
      let filteredDisplays = Display;
      if (subClass) {
        if (subClass === 'temp') {
          filteredDisplays = Display.filter(d => ['TempF', 'Humidity'].includes(d));
        } else if (subClass === 'wind') {
          filteredDisplays = Display.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
        }
      }

      // Create an object with all matching display values using the same logic as cards
      const displayValues = Array.isArray(filteredDisplays) 
        ? filteredDisplays.reduce((acc, key) => {
            // Use the same logic as the cards
            const value = getSensorValue(parsedJInfo, sensorInfo?.Type || 'Weather', key);
            acc[key] = value;
            return acc;
          }, {} as Record<string, number>)
        : { [filteredDisplays]: getSensorValue(parsedJInfo, sensorInfo?.Type || 'Weather', filteredDisplays) };

      return {
        timestamp: entry.Timestamp,
        ...displayValues
      };
    }) || [];

    // A fresh history load resets the live-reading dedupe key so the current
    // reading is re-appended onto this dataset rather than dropped.
    lastLiveReadingRef.current = '';
    setEsensorChart(prev => [...updatedEsensorChart]);
  }

  // const renderLoadingOrError = () => {
  //   if (isFetching) {
  //     return <p>Loading...</p>;
  //   } else if (isError) {
  //     return <p>Error: {error.message}</p>;
  //   }
  //   return null;
  // };

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'center' }}>
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {chartTitle ? (
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, mb: 0.5 }}>
            {chartTitle}
          </Typography>
        ) : null}
        {deviceEUI ? (
          <>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!isFetching && esensorChartData.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: compact ? 320 : 400, textAlign: 'center', px: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    {selectedRange && selectedRange !== 'Custom'
                      ? `No data in the ${selectedRange.toLowerCase()}`
                      : 'No data in the selected time range'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    This sensor hasn't reported during this period. Try a wider range.
                  </Typography>
                </Box>
              ) : (
                <SensorGraph 
                  deviceEUI={deviceEUI} 
                  Display={getFilteredDisplays()}
                  sensorColor={sensorColor}
                  startDateTime={startDateTime}
                  endDateTime={endDateTime}
                  height={compact ? 320 : 400}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: compact ? 1 : 0, flexWrap: 'wrap' }}>
              {!hideTimePicker ? (
                <GraphTimePicker
                  startDateTime={startDateTime}
                  endDateTime={endDateTime}
                  setStartDateTime={setStartDateTime}
                  setEndDateTime={setEndDateTime}
                  selectedRange={selectedRange}
                  setSelectedRange={setSelectedRange}
                />
              ) : null}
              <DownloadButton
                data={esensorChartData.map((item: EsensorChart) => ({
                  Timestamp: item.timestamp,
                  ...(Array.isArray(Display) ? Display.reduce((acc, key) => ({
                    ...acc,
                    [key]: item[key]
                  }), {}) : { [Display]: item[Display] })
                }))}
                filename={`${deviceEUI}_${Array.isArray(Display) ? Display.join('_') : Display}_${startDateTime.format('YYYY-MM-DD')}_to_${endDateTime.format('YYYY-MM-DD')}`}
                timestampField="Timestamp"
                valueField={Array.isArray(Display) ? Display.join(',') : Display}
                disabled={!esensorChartData.length}
                timezone="America/New_York"
              />
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 280 }}>
            <Typography variant="h6" color="text.secondary">
              Click on a sensor card to view its data
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default EsensorChartHandler;
